/**
 * ValuesIncubatorPage — flagship product detail page for ValuesIncubator+.
 *
 * As of M3 (cms_product_pages migration) the entire body comes from the
 * generated `cmsProductPages` snapshot:
 *   - chrome (badge/title/subtitle/audience/cta) from cms_products
 *   - detail body (modules + images + coreFeatures + stakeholders) from
 *     cms_product_pages.blocks_zhHant JSON tree
 *   - closing CTA scalars from cms_product_pages.cta_* columns
 *
 * `imageSrcMap` (the static-asset resolver) is GONE — images now resolve
 * through `cmsMediaUrl(media_uuid)` which honours any uuid that the
 * migrate-static-product-images.mts script has registered in cms_media.
 *
 * The page is structured so a draft preview_token renders without a
 * published row: the public route accepts a token claim and bypasses
 * the `status='published'` filter. previewTokenParam() in
 * src/utils/previewToken.ts is the same i18n-aware helper used by other
 * detail pages.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Card, Reveal, PageHeader, MobileTableCards } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import { SITE } from '@/content/site'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildCourse } from '@/utils/structuredData'
import { cmsProducts } from '@/content/cms/products.generated'
import { localizeProduct } from '@/content/cms/productLocale'
import { cmsProductPages, type CmsProductPageIndexEntry } from '@/content/cms/product-pages.generated'
import {
  fetchLiveProductPage,
  localeOf,
  productPageCopy,
} from '@/content/cms/productPageLocale'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import CmsImage from '@/components/media/CmsImage'
import PriorityValuesCards from '@/components/products/PriorityValuesCards'
import { accentFor, accentForModule } from '@/content/priorityValues'

/**
 * The inline custom properties that voice a section in one value's colour.
 *
 * `--vi-accent` is the PRINTED colour (fills, washes) and `--vi-accent-deep`
 * its INK form (anything with words on it). Both come from the deck, so a
 * section's colour is never a free-floating hex — see
 * src/content/priorityValues.ts for which value each section speaks in.
 */
function accentStyle(key: string): React.CSSProperties {
  const { hue, ink } = accentFor(key)
  return { '--vi-accent': hue, '--vi-accent-deep': ink } as React.CSSProperties
}

/** The same, for a CMS module — keyed by its id, falling back to deck order. */
function moduleAccentStyle(id: string, index: number): React.CSSProperties {
  const { hue, ink } = accentForModule(id, index)
  return { '--vi-accent': hue, '--vi-accent-deep': ink } as React.CSSProperties
}

/**
 * The `sizes` for a module image.
 *
 * One image is capped at `max-w-md` — 448px. Two or more share a
 * `md:grid-cols-2` grid inside the 1200px container, so a column is ≈588px
 * there, and '50vw' (600px at that viewport, more on a wider one)
 * overstates it. Overstating is the safe direction: a `sizes` that is too
 * small makes the browser upscale a too-small file.
 */
const SINGLE_IMAGE_SIZES = '(min-width: 448px) 448px, 100vw'
const TWO_UP_IMAGE_SIZES = '(min-width: 768px) 50vw, 100vw'

interface ModuleImage {
  media_uuid: string
  alt: string
  caption: string
  width: number
  height: number
}
interface ModuleFeature { title: string; description: string; highlight: string }
interface Module {
  id: string
  name: string
  tagline: string
  intro: string
  images: ModuleImage[]
  features: ModuleFeature[]
}
interface ViBlocks {
  coreFeaturesTag: string
  coreFeaturesTitle: string
  stakeholdersTag: string
  stakeholdersTitle: string
  modules: Module[]
  coreFeatures: Array<{ title: string; text: string }>
  stakeholders: Array<{ role: string; value: string }>
  tableHeaders: { feature: string; description: string; highlight: string; stakeholder: string; value: string }
}

/** Fall back to live API when the generated snapshot is empty (cold cache,
 *  or sync was skipped). Lets the page render even before `npm run
 *  cms:sync` regenerates the file. The live fetch asks for the ACTIVE
 *  locale — `fetchLiveProductPage` also pulls the zh-Hant copy alongside it
 *  and merges the two into one `locales` map, so the resolver falls back
 *  exactly as it does for the build-time snapshot. */
export default function ValuesIncubatorPage() {
  const { t, i18n } = useTranslation()
  const locale = localeOf(i18n.language)

  // The generated snapshot covers the normal case; live fetch covers
  // preview tokens and cold start. Pick whichever has the data.
  const initialPage = cmsProductPages.find((p) => p.product_slug === 'values-incubator') ?? null
  const rawProduct = cmsProducts.find((p) => p.slug === 'values-incubator') ?? null
  // The generated index carries all three locales' copy — resolve the
  // title/description/badge to the active locale.
  const initialProduct = rawProduct ? localizeProduct(rawProduct, locale) : null

  const [page, setPage] = useState<CmsProductPageIndexEntry | null>(initialPage)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (initialPage) return // generated snapshot already has it
    void fetchLiveProductPage('values-incubator', locale).then((live) => {
      if (cancelled) return
      if (live) {
        setPage(live)
      } else {
        setMissing(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [initialPage, locale])

  // The generated index carries all three locales' copy — resolve the body
  // and the chrome scalars to the active locale.
  const copy = page ? productPageCopy(page, locale) : null
  const blocks = copy?.blocks as ViBlocks | undefined
  const modules = blocks?.modules ?? []
  const coreFeatures = blocks?.coreFeatures ?? []
  const stakeholders = blocks?.stakeholders ?? []
  const tableHeaders = blocks?.tableHeaders ?? {
    feature: '功能',
    description: '說明',
    highlight: '特色',
    stakeholder: '持份者',
    value: '價值',
  }

  const title = initialProduct?.title ?? t('products.offers.valuesIncubator.title')
  const tagline = initialProduct?.description ?? t('products.offers.valuesIncubator.tagline')
  const subtitle = copy?.subtitle || t('products.offers.valuesIncubator.subtitle')
  const summary = copy?.summary || t('products.offers.valuesIncubator.summary')
  const heroAlt = copy?.hero_alt || t('products.offers.valuesIncubator.imageAlt')

  const whatsappHref = `${SITE.contact.whatsapp.link}?text=${encodeURIComponent(title)}`
  void heroAlt // reserved for a future hero image section; currently no hero

  return (
    <div className="vi-root">
      <SEO
        title={t('seo.valuesIncubatorTitle')}
        description={t('seo.valuesIncubatorDescription')}
        keywords={t('seo.valuesIncubatorKeywords')}
        jsonLd={[
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.products'), path: '/products' },
            { name: 'ValuesIncubator+', path: '/products/values-incubator' },
          ]),
          buildCourse({
            path: '/products/values-incubator',
            name: 'ValuesIncubator+ — 價值觀培養方案',
            description: t('seo.valuesIncubatorDescription'),
            educationalLevel: 'Secondary',
            keywords: ['價值觀培養', '品格教育', '學校系統化', 'YADA'],
          }),
        ]}
      />
      {/* No `tag`: the badge (「旗艦方案」) was painted above the title and
          read as a second, redundant label. 2026-11-04: the hero opens
          straight on the title and the subtitle. */}
      <PageHeader title={title} description={subtitle}>
        {/* The deck's colour code — the twelve printed colours in deck
            order, under the title. It is the page's spine: the same
            order as the fan further down and as the printable sheet. */}
        <span className="vi-ribbon" aria-hidden="true" />
      </PageHeader>

      {missing && (
        <section className="py-section-y px-section-x bg-surface">
          <Container size="lg">
            <p className="text-center text-ink-muted">
              尚未在 CMS 建立 ValuesIncubator 詳情頁。請先到後台「產品詳情」新增；或執行 <code className="px-1 bg-surface-2 rounded">npm run cms:sync</code>。
            </p>
          </Container>
        </section>
      )}

      {/* Lead paragraph */}
      <section
        className="vi-accent py-section-y px-section-x bg-surface"
        style={accentStyle('lead')}
        aria-labelledby="vi-lead-title"
      >
        <Container size="lg">
          <Reveal>
            <div className="text-center mb-7">
              <span className="section-title__tagline inline-block mb-3">{tagline}</span>
              <h2 id="vi-lead-title" className="section-title__title text-2xl md:text-3xl text-ink max-w-3xl mx-auto leading-snug">
                {summary}
              </h2>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* The physical deck — 12 printed value cards. Placed right after the
          lead paragraph so a head of school meets a real object before the
          software screens. */}
      <PriorityValuesCards />

      {/* 4 functional modules */}
      {modules.map((mod, mIdx) => (
        <section
          key={mod.id}
          id={`module-${mod.id}`}
          className={`vi-accent py-section-y px-section-x scroll-mt-[var(--sticky-header-offset)] ${
            mIdx % 2 === 0 ? 'bg-surface-2' : 'bg-surface'
          }`}
          style={moduleAccentStyle(mod.id, mIdx)}
          aria-labelledby={`module-${mod.id}-title`}
        >
          <Container>
            <Reveal>
              <div className="text-center mb-7">
                <span className="section-title__tagline inline-block mb-3">{mod.tagline}</span>
                <h2
                  id={`module-${mod.id}-title`}
                  className="section-title__title text-3xl md:text-4xl text-ink"
                >
                  {mod.name}
                </h2>
                <p className="mt-4 text-lg text-ink-soft max-w-2xl mx-auto leading-relaxed">
                  {mod.intro}
                </p>
              </div>
            </Reveal>

            {/* Image grid (1-up or 2-up depending on image count) */}
            {mod.images.length > 0 && (
              <Reveal>
                <div
                  className={`grid gap-6 mb-7 ${
                    mod.images.length === 1
                      ? 'grid-cols-1 max-w-md mx-auto'
                      : 'grid-cols-1 md:grid-cols-2'
                  }`}
                >
                  {/* One image is capped at `max-w-md` (448px); two or more
                      share a `md:grid-cols-2` grid inside the 1200px
                      container, so a column is ≈588px there and '50vw'
                      (600px) overstates it — the safe direction. */}
                  {mod.images.map((img, imgIdx) => {
                    const url = cmsMediaLargestWebpUrl(img.media_uuid)
                    return (
                      <figure
                        key={`${mod.id}-${imgIdx}-${img.media_uuid}`}
                        className="vi-figure bg-surface-2 border border-border rounded-yada-xl overflow-hidden shadow-yada"
                      >
                        {url ? (
                          <CmsImage
                            uuid={img.media_uuid}
                            alt={img.alt}
                            sizes={
                              mod.images.length === 1
                                ? SINGLE_IMAGE_SIZES
                                : TWO_UP_IMAGE_SIZES
                            }
                            className="block w-full h-auto aspect-[3/2] object-contain"
                          />
                        ) : (
                          <div
                            className="aspect-[3/2] flex items-center justify-center text-ink-muted text-sm"
                            aria-label={img.alt}
                          >
                            {img.caption || img.alt}
                          </div>
                        )}
                        <figcaption className="text-sm text-ink-muted px-4 py-3 text-center border-t border-border bg-surface-3 leading-snug">
                          {img.caption}
                        </figcaption>
                      </figure>
                    )
                  })}
                </div>
              </Reveal>
            )}

            {/* 3-column feature table */}
            <Reveal>
              <div className="max-w-4xl mx-auto">
                <MobileTableCards
                  data={mod.features.map((f, i) => ({ ...f, _idx: i }))}
                  rowKey="_idx"
                  columns={[
                    {
                      header: tableHeaders.feature,
                      field: 'title',
                    },
                    {
                      header: tableHeaders.description,
                      field: 'description',
                    },
                    {
                      header: tableHeaders.highlight,
                      field: 'highlight',
                      render: (val) => (
                        <span className="vi-hi font-semibold">{String(val ?? '')}</span>
                      ),
                    },
                  ]}
                />
              </div>
            </Reveal>
          </Container>
        </section>
      ))}

      {/* 4 system core features (pills) */}
      <section
        className="vi-accent py-section-y px-section-x bg-surface-2"
        style={accentStyle('coreFeatures')}
        aria-labelledby="vi-core-features-title"
      >
        <Container>
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">
                {blocks?.coreFeaturesTag ?? '系統核心特色'}
              </span>
              <h2 id="vi-core-features-title" className="section-title__title text-3xl md:text-4xl text-ink">
                {blocks?.coreFeaturesTitle ?? '為何選擇 ValuesIncubator+'}
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-gap-cards">
            {coreFeatures.map((feat, idx) => (
              <Reveal key={idx} delay={idx * 0.08}>
                <Card bordered className="h-full">
                  <div className="vi-tile w-12 h-12 rounded-yada-lg flex items-center justify-center mb-4">
                    <Icon icon="lucide:shield-check" className="vi-tile__icon text-2xl" />
                  </div>
                  <H3 className="mb-2 leading-snug">{feat.title}</H3>
                  <p className="text-sm text-ink-soft leading-relaxed">{feat.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Stakeholder value table */}
      <section
        className="vi-accent py-section-y px-section-x bg-surface"
        style={accentStyle('stakeholders')}
        aria-labelledby="vi-stakeholders-title"
      >
        <Container>
          <Reveal>
            <div className="text-center mb-7">
              <span className="section-title__tagline inline-block mb-3">
                {blocks?.stakeholdersTag ?? '對學校帶來的價值'}
              </span>
              <h2 id="vi-stakeholders-title" className="section-title__title text-3xl md:text-4xl text-ink">
                {blocks?.stakeholdersTitle ?? '對每一位持份者的承諾'}
              </h2>
            </div>
          </Reveal>
          <Reveal>
            <div className="max-w-3xl mx-auto">
              <MobileTableCards
                data={stakeholders.map((s, i) => ({ ...s, _idx: i }))}
                rowKey="_idx"
                columns={[
                  {
                    header: tableHeaders.stakeholder,
                    field: 'role',
                  },
                  {
                    header: tableHeaders.value,
                    field: 'value',
                  },
                ]}
              />
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Closing CTA — the whole deck, closing the page. Every stop of
          the gradient is an INK colour, so the cream text stays above
          4.5:1 from end to end. */}
      <section className="vi-cta py-section-y px-section-x text-surface">
        <Container>
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              {/* Cream, not gold: the gold accent only clears 2.6:1 on two
                  of the gradient's stops. The gold BUTTON below is where
                  the accent belongs on this surface. */}
              <span className="inline-block text-surface text-sm font-bold uppercase tracking-[0.2em] mb-3">
                {copy?.cta.tag || '立即行動'}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {copy?.cta.title || '準備好建立品格文化了嗎？'}
              </h2>
              <p className="text-surface leading-relaxed mb-8">
                {copy?.cta.text || ''}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent text-ink px-7 py-3 rounded-yada-full font-semibold hover:bg-accent-soft transition-colors"
                >
                  <Icon icon="lucide:message-circle" />
                  {copy?.cta.whatsapp_label || 'WhatsApp 聯絡我們'}
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-surface text-surface px-7 py-3 rounded-yada-full font-semibold hover:bg-surface hover:text-ink transition-colors"
                >
                  {copy?.cta.form_label || '填寫聯絡表單'}
                  <Icon icon="lucide:arrow-right" />
                </a>
              </div>
              <p className="mt-7 text-surface font-semibold italic text-lg">
                {copy?.cta.closing || ''}
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Back to Products button */}
      <section className="py-section-y px-section-x bg-surface">
        <Container>
          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-ink-soft hover:text-primary transition-colors font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
              <span>{t('products.backToProducts')}</span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  )
}
